import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { loginAdmin, verifyAdminToken } from "@/lib/admin-auth.functions";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/login")({
  head: () => ({
    meta: [
      { title: "Admin Sign In — RanchPlanner" },
      { name: "description", content: "Admin sign-in for RanchPlanner staff. This page is restricted and not intended for visitors." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLoginPage,
});

const TOKEN_KEY = "rp_admin_token";

function AdminLoginPage() {
  const navigate = useNavigate();
  const loginFn = useServerFn(loginAdmin);
  const verifyFn = useServerFn(verifyAdminToken);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);

  // If already logged in, bounce straight to admin
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const existing = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
      if (!existing) {
        if (!cancelled) setChecking(false);
        return;
      }
      try {
        const res = await verifyFn({ data: { token: existing } });
        if (!cancelled && res.valid) {
          navigate({ to: "/admin/events" });
          return;
        }
      } catch {
        // ignore
      }
      if (!cancelled) {
        localStorage.removeItem(TOKEN_KEY);
        setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, verifyFn]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await loginFn({ data: { password } });
      if (res.ok) {
        localStorage.setItem(TOKEN_KEY, res.token);
        toast.success("Signed in");
        navigate({ to: "/admin/events" });
      } else {
        toast.error(res.error || "Sign in failed");
        setPassword("");
      }
    } catch (err: any) {
      toast.error(err?.message || "Sign in failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 mb-4">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-3xl font-black tracking-tight">Admin Sign In</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Enter the admin password to continue.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="admin-password" className="sr-only">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              maxLength={200}
              placeholder="Password"
              className="w-full px-4 py-3 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !password.trim()}
            className="w-full px-4 py-3 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitting ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
